import {
  Alert,
  Button,
  Checkbox,
  Modal,
  Typography,
  message
} from 'antd';
import { useEffect, useState } from 'react';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';

/**
 * The recovery codes, shown once and never again.
 *
 * One component, three entry points: confirming the authenticator, confirming the email
 * factor, and the Generate button on the codes popup and the settings card. Building it once
 * is the point: this is the only moment the codes exist anywhere the user can see them.
 *
 * They are stored server-side as salted digests and are **not recoverable, even for their
 * owner**: a set that could be re-read on demand would be a permanent second-factor bypass
 * sitting behind nothing but a session. So there is no "show my codes" screen anywhere in
 * this app, and this modal says so. Otherwise users go looking for one.
 *
 * Because it is shown once, it cannot be dismissed by accident: no `×`, no Esc, no
 * mask-close, and the only button is gated on an explicit acknowledgement.
 *
 * The codes stay in this component's props and the parent clears them on close. They never
 * reach `localStorage`, a log line, analytics or the URL.
 */

interface RecoveryCodesModalProps {
  open: boolean;
  codes: string[];
  /** Fired once the user confirms they have saved them. The parent clears the codes here. */
  onClose: () => void;
  /** Overrides the default heading: the regenerate path says something different. */
  title?: string;
  /** Shown above the codes. Defaults to the first-issue wording. */
  intro?: React.ReactNode;
}

const FILE_NAME = 'seeward-recovery-codes.txt';

const RecoveryCodesModal = ({
  open,
  codes,
  onClose,
  title = 'Save your recovery codes',
  intro
}: RecoveryCodesModalProps) => {
  const [hasSaved, setHasSaved] = useState(false);

  // A second set arriving into an open modal must not inherit the previous acknowledgement.
  useEffect(() => {
    if (open) setHasSaved(false);
  }, [open, codes]);

  const asText = codes.join('\n');

  const copyAll = async () => {
    try {
      // `navigator.clipboard` needs a secure context, which localhost counts as, but an
      // http:// deployment on a LAN address does not, and there the API is simply absent.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(asText);
      } else {
        const scratch = document.createElement('textarea');
        scratch.value = asText;
        scratch.setAttribute('readonly', '');
        scratch.style.position = 'fixed';
        scratch.style.opacity = '0';
        document.body.appendChild(scratch);
        scratch.select();
        document.execCommand('copy');
        document.body.removeChild(scratch);
      }
      message.success('Recovery codes copied.');
    } catch {
      message.error('Could not copy. Select the codes and copy them by hand.');
    }
  };

  const download = () => {
    const blob = new Blob(
      [
        'SeeWard recovery codes\n', 'Each code works once. Keep this file somewhere that is not your email.\n\n', `${asText}\n`
      ],
      { type: 'text/plain;charset=utf-8' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = FILE_NAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Otherwise the blob: the codes. Stays alive in the document for as long as the tab is.
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      open={open}
      title={title}
      // Shown once. Every accidental way out is closed on purpose.
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={520}
      footer={[
        <Button 
          key="done" 
          type="primary" 
          disabled={!hasSaved} 
          onClick={onClose}
        >
          I have saved these
        </Button>
      ]}
    >
      <Typography.Paragraph className="text-[13px]">
        {intro ?? (
          <>
            These ten codes are how you sign in if you lose your second factor{' '}
            <strong>and</strong> your email. Each one works once.
          </>
        )}
      </Typography.Paragraph>

      <Alert
        type="warning"
        showIcon
        className="mb-4"
        message={
          <span className="text-[13px]">
            <strong>Keep these somewhere that is not your email.</strong> That is the whole
            point of them: an emailed code already covers every other case.
          </span>
        }
      />

      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 rounded-md border border-solid
          border-[#d9d9d9] p-4 font-mono text-[14px] tracking-wider select-all"
      >
        {codes.map((codeValue) => (
          <div key={codeValue}>{codeValue}</div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button icon={<CopyOutlined />} onClick={copyAll}>
          Copy all
        </Button>
        <Button icon={<DownloadOutlined />} onClick={download}>
          Download .txt
        </Button>
      </div>

      <Typography.Paragraph 
        type="secondary" 
        className="text-[13px] mt-4 !mb-3"
      >
        This is the only time they are shown. We store them scrambled and cannot read them
        back: not even for you. If you lose them, generate a new set from the Security tab of
        your profile, which retires these.
      </Typography.Paragraph>

      <Checkbox
        checked={hasSaved}
        onChange={(event) => setHasSaved(event.target.checked)}
      >
        <span className="text-[13px]">
          I have saved these codes somewhere safe
        </span>
      </Checkbox>
    </Modal>
  );
};

export default RecoveryCodesModal;
